import type {
  CreatePlanningTaskInput,
  PlanningTaskBulkActionInput,
  UpdatePlanningTaskInput,
} from '@amore/shared'
import { queryOptions } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { api, unwrap } from '@/lib/api'

const workspaceApi = api.api.workspaces[':slug']

export const taskKeys = {
  all: (slug: string) => ['planning-tasks', slug] as const,
  members: (slug: string) => ['workspace-members', slug] as const,
}
export type TasksResponse = InferResponseType<(typeof workspaceApi)['planning-tasks']['$get'], 200>
export type PlanningTask = TasksResponse['tasks'][number]
export type MembersResponse = InferResponseType<typeof workspaceApi.members.$get, 200>
export type CreateTaskResponse = InferResponseType<
  (typeof workspaceApi)['planning-tasks']['$post'],
  201
>

export const tasksQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: taskKeys.all(slug),
    queryFn: () => unwrap<TasksResponse>(workspaceApi['planning-tasks'].$get({ param: { slug } })),
  })

export const taskMembersQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: taskKeys.members(slug),
    queryFn: () => unwrap<MembersResponse>(workspaceApi.members.$get({ param: { slug } })),
  })

export function createTaskRequest(slug: string, json: Omit<CreatePlanningTaskInput, 'sortOrder'>) {
  return unwrap<CreateTaskResponse>(
    workspaceApi['planning-tasks'].$post({ param: { slug }, json: { ...json, sortOrder: 0 } }),
  )
}

export function updateTaskRequest(slug: string, taskId: string, json: UpdatePlanningTaskInput) {
  return unwrap(
    workspaceApi['planning-tasks'][':taskId'].$patch({
      param: { slug, taskId },
      json,
    }),
  )
}

export function deleteTaskRequest(slug: string, taskId: string) {
  return workspaceApi['planning-tasks'][':taskId'].$delete({ param: { slug, taskId } })
}

export function bulkActionTasksRequest(slug: string, json: PlanningTaskBulkActionInput) {
  return unwrap(
    workspaceApi['planning-tasks']['bulk-actions'].$post({
      param: { slug },
      json,
    }),
  )
}
